/**
 * Clash-Off bracket logic.
 *
 * Given N contestants (artists whose sets overlap), run a single-elimination
 * bracket where each match is a 1v1 head-to-head. Odd numbers of contestants
 * at any round get a bye to the next round.
 *
 * Pure data — the React component drives it with `resolveMatch` and
 * `activeMatch`.
 */

export type ClashContestant = {
  slug: string;
  name: string;
  stage: string;
};

export type ClashMatch = {
  a: ClashContestant;
  /** null when `a` has a bye. */
  b: ClashContestant | null;
  /** null until resolved (via pick or bye). */
  winner: ClashContestant | null;
  /** True when winner was assigned automatically via a bye. */
  bye: boolean;
};

export type ClashRound = {
  matches: ClashMatch[];
};

export type ClashBracket = {
  rounds: ClashRound[];
  champion: ClashContestant | null;
};

/**
 * Seed a fresh bracket for N contestants (N >= 2). Bye matches are
 * auto-resolved at creation time.
 */
export function buildBracket(contestants: ClashContestant[]): ClashBracket {
  if (contestants.length < 2) {
    return { rounds: [], champion: null };
  }
  const first = pairUp(contestants);
  const bracket: ClashBracket = { rounds: [first], champion: null };
  // If round 1 is all byes (only possible when N === 1 which we already
  // guard against), normalise. Still, fold any fully-resolved round to
  // generate follow-ups.
  return fold(bracket);
}

/** Returns the next match awaiting a user decision, or null if the bracket is done. */
export function activeMatch(
  bracket: ClashBracket,
): { roundIndex: number; matchIndex: number; match: ClashMatch } | null {
  if (bracket.champion) return null;
  for (let r = 0; r < bracket.rounds.length; r++) {
    const round = bracket.rounds[r]!;
    for (let m = 0; m < round.matches.length; m++) {
      const match = round.matches[m]!;
      if (match.winner === null) return { roundIndex: r, matchIndex: m, match };
    }
  }
  return null;
}

/** Resolve the currently active match with the chosen winner. */
export function resolveMatch(
  bracket: ClashBracket,
  winner: ClashContestant,
): ClashBracket {
  const active = activeMatch(bracket);
  if (!active) return bracket;
  const { roundIndex, matchIndex, match } = active;
  // Sanity: winner must be one of the two contestants.
  if (winner.slug !== match.a.slug && winner.slug !== match.b?.slug) {
    return bracket;
  }
  const newRounds = bracket.rounds.map((round, ri) => {
    if (ri !== roundIndex) return round;
    return {
      matches: round.matches.map((m, mi) =>
        mi === matchIndex ? { ...m, winner, bye: false } : m,
      ),
    };
  });
  return fold({ rounds: newRounds, champion: bracket.champion });
}

/**
 * Final slug lists after the bracket ends:
 *  - winnerSlug: the champion (or null if not yet decided)
 *  - loserSlugs: every contestant who lost at least one match
 *
 * Contestants who never got a decisive match (pure byes all the way) are
 * neither winner nor loser.
 */
export function bracketResult(bracket: ClashBracket): {
  winnerSlug: string | null;
  loserSlugs: string[];
} {
  const losers = new Set<string>();
  for (const round of bracket.rounds) {
    for (const m of round.matches) {
      if (!m.winner || m.bye || !m.b) continue;
      // Whichever contestant is not the winner is a loser.
      if (m.a.slug !== m.winner.slug) losers.add(m.a.slug);
      if (m.b.slug !== m.winner.slug) losers.add(m.b.slug);
    }
  }
  if (bracket.champion) losers.delete(bracket.champion.slug);
  return {
    winnerSlug: bracket.champion?.slug ?? null,
    loserSlugs: [...losers],
  };
}

/** Total user-decidable matches required to finish this bracket from its current state. */
export function matchesRemaining(bracket: ClashBracket): number {
  if (bracket.champion) return 0;
  let count = 0;
  for (const round of bracket.rounds) {
    for (const m of round.matches) {
      if (m.winner === null) count++;
    }
  }
  return count;
}

// ---------- internals ----------

/** Pair contestants for a single round; odd-count tail contestant gets a bye. */
function pairUp(contestants: ClashContestant[]): ClashRound {
  const matches: ClashMatch[] = [];
  for (let i = 0; i < contestants.length; i += 2) {
    const a = contestants[i]!;
    const b = contestants[i + 1] ?? null;
    matches.push({
      a,
      b,
      winner: b === null ? a : null,
      bye: b === null,
    });
  }
  return { matches };
}

/**
 * If the last round is fully resolved, synthesize the next round (or crown
 * the champion). Applied repeatedly because a round of all byes cascades.
 */
function fold(bracket: ClashBracket): ClashBracket {
  let rounds = bracket.rounds;
  let champion = bracket.champion;
  while (true) {
    const last = rounds[rounds.length - 1];
    if (!last || last.matches.some(m => m.winner === null)) break;
    const winners = last.matches.map(m => m.winner!);
    if (winners.length === 1) {
      champion = winners[0]!;
      break;
    }
    rounds = [...rounds, pairUp(winners)];
  }
  return { rounds, champion };
}
